import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import ReactGA from 'react-ga4';
////////////////////////////////////////////////////////////////////
import { useCustomDispatch, useCustomSelector } from '@/core/redux/hooks';
import {
	setActiveCollection,
	setActiveCollectionGroup,
} from '@/core/redux/slices';
import { slugify } from '@/app/shared/utils';
import { BackButton, CollectionsCarousel } from '@/app/shared/components';

/**
 * Component for rendering a page of stickers.
 *
 * @remarks
 * This component renders a page containing a list of collections and their
 * respective stickers. It also sends a ReactGA event when mounted.
 *
 * @returns A JSX element containing the sticker list.
 */
const Stickers: React.FC = () => {
	/*********************
	// #region Hooks
	**********************/
	const { group, collection } = useParams();
	const dispatch = useCustomDispatch();

	/*********************
	// #region Redux
	**********************/
	const {
		activeCollectionGroup,
		activeCollection,
		activeCollectionPosition,
	} = useCustomSelector((state) => state.collections);

	/*********************
	// #region Observables
	**********************/
	useEffect(() => {
		ReactGA.send({
			hitType: 'pageview',
			page: '/collections',
			title: 'Stickers',
		});
	}, []);

	useEffect(() => {
		if (activeCollectionGroup || activeCollection) return;
		if (!group || !collection) return;
		dispatch(setActiveCollectionGroup(group));
		dispatch(
			setActiveCollection({
				groupId: group,
				collectionId: collection,
			})
		);
	}, [group, collection]);

	useEffect(() => {
		if (!activeCollectionGroup) return;
		if (!activeCollection) return;

		ReactGA.send({
			hitType: 'pageview',
			page: `/collection/${slugify(activeCollectionGroup.name)}/${slugify(
				activeCollection.title
			)}`,
			title: `Collection stickers ${activeCollection.title}`,
		});
	}, [activeCollectionGroup, activeCollection]);

	/*********************
	// #region Template
	**********************/
	return (
		<>
			<div className="flex flex-col pt-8 gap-4 mb-6 mx-4">
				<BackButton />
				<h2 className="text-center text-2xl font-bold">
					{activeCollection?.title}
				</h2>
				<div className=" px-6 text-[10px] text-center line-clamp-4">
					<p>{activeCollection?.description}</p>
				</div>
			</div>

			{activeCollectionGroup &&
				activeCollectionGroup.items.length > 0 && (
					<CollectionsCarousel
						collections={activeCollectionGroup.items}
						activeCollectionPosition={activeCollectionPosition || 0}
						groupId={group || ''}
					/>
				)}
		</>
	);
};

export default Stickers;
